import { borrowerCreditsDataModule } from '@perpay-web/fintech/dataModules/composed/borrowerCredits';
import { cardActivityDataModule } from '@perpay-web/fintech/dataModules/composed/cardActivity';
import { cardCreateAutoPayDataModule } from '@perpay-web/fintech/dataModules/composed/cardCreateAutoPay';
import { cardDetailsNotificationsDataModule } from '@perpay-web/fintech/dataModules/composed/cardDetailsNotifications';
import { cardManualPaymentSetupDataModule } from '@perpay-web/fintech/dataModules/composed/cardManualPaymentSetup';
import { cardOnboardingSwitcherDataModule } from '@perpay-web/fintech/dataModules/composed/cardOnboardingSwitcher';
import { cardPaymentSummaryDataModule } from '@perpay-web/fintech/dataModules/composed/cardPaymentSummary';
import { cardStatementsDataModule } from '@perpay-web/fintech/dataModules/composed/cardStatements';
import { checkoutDataModule } from '@perpay-web/fintech/dataModules/composed/checkout';
import { dashboardDataModule as dashboardV2DataModule } from '@perpay-web/fintech/dataModules/composed/dashboardV2';
import { directDepositDetailsDataModule } from '@perpay-web/fintech/dataModules/composed/directDepositDetails';
import { directDepositUpdateEmployerAndAmountDataModule } from '@perpay-web/fintech/dataModules/composed/directDepositUpdateEmployerAndAmount';
import { directDepositUpdateManualPaymentSetupDataModule } from '@perpay-web/fintech/dataModules/composed/directDepositUpdateManualPaymentSetup';
import { fetchCardDetailsV2DataModule } from '@perpay-web/fintech/dataModules/composed/fetchCardDetailsV2';
import { marketplaceDetailsDataModule } from '@perpay-web/fintech/dataModules/composed/marketplaceDetails';
import { marketplaceDetailsNotificationsDataModule } from '@perpay-web/fintech/dataModules/composed/marketplaceDetailsNotifications';
import { ordersDataModule } from '@perpay-web/fintech/dataModules/composed/orders';
import { partnerOnboardingDataModule } from '@perpay-web/fintech/dataModules/composed/partnerOnboarding';
import { payrollInstructionsFlowDataModule } from '@perpay-web/fintech/dataModules/composed/payrollInstructionsFlow';
import { perpayPlusDataModule } from '@perpay-web/fintech/dataModules/composed/perpayPlus';
import { pinwheelDataModule } from '@perpay-web/fintech/dataModules/composed/pinwheel';
import { updateDirectDepositDataModule } from '@perpay-web/fintech/dataModules/composed/updateDirectDeposit';
import { bankAccountsForCardAutoPayDataModule } from '@perpay-web/fintech/dataModules/crud/bankAccountsForCardAutoPay';
import { cardAccountApplicationCrudDataModule } from '@perpay-web/fintech/dataModules/crud/cardAccountApplication';
import { featureEnrollmentsForPerpayPlusCrudDataModule } from '@perpay-web/fintech/dataModules/crud/featureEnrollmentsForPerpayPlus';
import { userInfoForCardOnboardingMVP2CrudDataModule } from '@perpay-web/fintech/dataModules/crud/userInfoForCardOnboardingMVP2';
import { fetchTrustPilotLink } from '@perpay-web/fintech/epics/apis/trustPilotLink';
import {
    analyticsIterablePushNotificationOpen,
    navigateOnCordovaUrlPush,
    navigateOnNativeUrlPush,
} from '@perpay-web/fintech/epics/utils/push';
import {
    actionsToNativeFromWeb,
    actionsToWebFromNative,
} from '@perpay-web/native-client/epics';
import { EpicManager } from '@perpay-web/services/EpicManager';
import { hookActionEpic } from '@perpay-web/services/singletons';
import {
    cleanupAlgoliaOnLogout,
    cleanupMagentoTokenOnLogout,
} from '@perpay-web/storefront/epics/authentication/storefrontTokens';

import { reportABTestVariant as reportABTestVariantForDataModule } from './analytics/abTests';
import {
    getABTest,
    reportABTestVariant,
    fetchABTests,
    fetchABTestsV2,
} from './apis/abTests';
import { getAccountDeactivationEligibility } from './apis/accountDeactivationEligibility';
import { getAccountDeactivationReasons } from './apis/accountDeactivationReasons';
import { fetchAccountSummary } from './apis/accountSummary';
import { fetchAds } from './apis/ads';
import { analyticsBuffer } from './apis/analytics';
import { appUpdate } from './apis/appUpdate';
import {
    authenticationSuccess,
    login,
    refreshToken,
    logoutToken,
    resetPassword,
    recaptchaActive,
    recaptchaReset,
    logoutAuthService,
    initializeIdentity,
    resendMFA,
    verifyMFA,
    passkeyAuthenticateCompleteSuccess,
    fetchAuthTokensSuccess,
    passkeyRegisterLogin,
    passkeyRegisterSignup,
    openBiometricRegistrationFailureModal,
    loginWaf,
    wafCaptchaActive,
} from './apis/authentication';
import { fetchInitiatedAutoPayPayments } from './apis/autoPayPayments';
import {
    createAutoPaySchedule,
    fetchAutoPaySchedules,
    updateAutoPaySchedule,
} from './apis/autoPaySchedules';
import {
    fetchBanks,
    createBank,
    verifyBank,
    createBankAccount,
    fetchBankAccounts,
} from './apis/banks';
import { fetchBorrowerCreditSummary } from './apis/borrowerCreditSummary';
import { fetchBorrowerCredits } from './apis/borrowerCredits';
import {
    createCardAccountApplications,
    fetchCardAccountApplication,
    pollCardAccountApplicationStatus,
    submitCardApplication,
    submitCardApplicationError,
    updateCardAccountApplication,
    pollCardApplicationStatus,
    pollCardApplicationStatusRedirect,
    pollCardApplicationStatusTimeoutRedirect,
} from './apis/cardAccountApplications';
import { fetchCardAccount } from './apis/cardAccounts';
import { cardActivate } from './apis/cardActivation';
import { fetchCardActivity } from './apis/cardActivity';
import { fetchCardDashboardSummary } from './apis/cardDashboardSummary';
import { uploadCardDocuments } from './apis/cardDocuments';
import {
    validateAddressForCardMigration,
    handleCardMigrationSuccess,
} from './apis/cardMigration';
import {
    downloadCardStatement,
    fetchCardStatements,
} from './apis/cardStatements';
import { fetchCompanies, fetchCompaniesSelfOnboarding } from './apis/companies';
import { fetchCoreSummary } from './apis/coreSummary';
import { fetchCreditLimits, updateCreditLimit } from './apis/creditLimits';
import { fetchCreditScoreHistory } from './apis/creditScoreHistory';
import { fetchCreditScoresSummary } from './apis/creditScoresSummary';
import { fetchPlatformDashboardNotifications } from './apis/dashboardPlatformNotifications';
import {
    createDeduction,
    fetchDeductions,
    fetchDeductionsForDataModule,
    pinwheelTransitionToModal,
    pinwheelSplitTransitionToModal,
    cardPinwheelTransitionToModal,
    updateDirectDepositPinwheelTransitionToModal,
    updateDeduction,
    updateDeductionAccountId,
} from './apis/deductions';
import { submitDefaultAddress } from './apis/defaultAddress';
import { createDeposit } from './apis/deposits';
import { fetchDirectDepositDashboardSummary } from './apis/directDepositSummary';
import { createEvent } from './apis/events';
import { fetchExternalCards, createExternalCard } from './apis/externalCards';
import {
    enrollFeature,
    reEnableFeature,
    fetchEnrolledFeatures,
    disableFeature,
    fetchFeatureEnrollments,
    createFeatureEnrollment,
} from './apis/featureEnrollments';
import { fetchFeatureSwitch } from './apis/featureSwitch';
import { fetchFeatures } from './apis/features';
import { fetchGeneralAccountSummary } from './apis/generalAccountSummary';
import { verifyIdentity } from './apis/identityVerification';
import {
    fetchPayrollInstructions,
    fetchPayrollInstructionsForDataModule,
} from './apis/instructions';
import {
    getJobs,
    checkoutUpdateEmployeeID,
    createPrimaryJob,
    fetchJobsForDataModule,
    updateJob,
} from './apis/jobs';
import { fetchLearnMore } from './apis/learnMore';
import { fetchMarketplaceDashboardSummary } from './apis/marketplaceDashboardSummary';
import {
    acknowledgeNotification,
    fetchNotifications,
} from './apis/notifications';
import { getOrderCancellationReasons } from './apis/orderCancellationReasons';
import { checkoutLoanDecision } from './apis/orderReview';
import {
    fetchOrders,
    checkoutCreateOrder,
    checkoutUpdateOrder,
    cancelOrderFromModal,
    refreshOrder,
    fetchOrdersForDataModule,
} from './apis/orders';
import {
    partnerOnboardingSignup,
    verifyPartnerOnboardingCode,
    partnerOnboardingSubmitCardApplication,
    createPassword,
} from './apis/partnerOnboarding';
import { createPaymentPlan } from './apis/paymentPlans';
import { fetchPaymentSetupSummary } from './apis/paymentSetupSummary';
import { fetchActivePayments } from './apis/payments';
import { createPaystub, pollPaystub } from './apis/paystubs';
import { fetchPerpayPlusDashboardSummary } from './apis/perpayPlusDashboardSummary';
import { fetchPinwheelABTest } from './apis/pinwheelABTest';
import { fetchPinwheelEligibility } from './apis/pinwheelEligibility';
import { fetchReferralStats } from './apis/referralStats';
import {
    primaryJob,
    salaryInfo,
    setPhone,
    signup,
    signupWaf,
    verifyPhone,
} from './apis/signup';
import { createSurveyResponse } from './apis/surveyResponses';
import { fetchPinwheelSurvey } from './apis/surveys';
import { fetchTransactionDetails } from './apis/transactionDetails';
import {
    fetchUserInfo,
    updateUserInfo,
    checkoutUpdateUserInfo,
    checkoutVerifyIdentity,
    deactivateAccount,
    cardUpdateUserInfo,
    fetchUserInfoForDataModule,
    updateUserInfoForDataModule,
    updateUserInfoContextSuccess,
    cardApplicationUpdateUserInfo,
    cardApplicationUpdateUserInfoSuccess,
} from './apis/userInfo';
import {
    fetchUserStatus,
    fetchUserStatusForDataModule,
} from './apis/userStatus';
import { validateAddressesForMVP2 } from './apis/validateAddress';
import {
    createWithdrawalRequest,
    fetchWithdrawalBalance,
    fetchWithdrawalBalanceV2,
} from './apis/withdrawal';
import { reportError } from './errors';
import {
    postAuthRedirect,
    routeToJwtLocation,
    routeToLocation,
} from './router';
import { fetchAddressDetailsForDataModule } from './ui/addressDetails';
import { fetchAddressSuggestionsForDataModule } from './ui/addressSuggestions';
import {
    updateCardAutoPayStepAfterDateSelection,
    updateCardAutoPayStateAfterFrequencySelection,
    cancelAutoPaySchedule,
    createAutoPayScheduleForCardAutoPay,
} from './ui/autoPaySchedules';
import {
    bankScreenCreateBank,
    createBankAccountForCardAutoPay,
    fetchBankAccountsForCardAutoPay,
    verifySelectBank,
    verifySingleBank,
} from './ui/banks';
import { fetchCardActivation } from './ui/cardActivation';
import { cardConfirmEmployerSetNextStep } from './ui/cardConfirmEmployer';
import {
    cardPinwheelTransitionToModalMVP2,
    creditUnfreezeCardAccountApplication,
    fetchCardAccountApplicationForOnboarding,
    fetchFeaturesForOnboarding,
    processCardApplication,
    processCardApplicationAddress,
    updateCardAccountApplicationForOnboarding,
} from './ui/cardOnboarding';
import { cardOnboardingMetalReplaceMaterial } from './ui/cardOnboardingMetal';
import {
    updateEmploymentUI,
    updatePaycycleUI,
    cardUpdateSalaryInfo,
    cardCreatePrimaryJob,
} from './ui/employment';
import { profileCreateExternalCard } from './ui/externalCards';
import {
    createFeatureEnrollmentForPerpayPlus,
    fetchFeatureEnrollmentsForPerpayPlus,
} from './ui/featureEnrollments';
import { gateCardAccount } from './ui/gateCardAccount';
import { gateCardApply } from './ui/gateCardApply';
import {
    fetchPartnerHostedCardApplication,
    partnerOnboardingPollApplicationStatus,
} from './ui/partnerOnboardingCardApplication';
import { paymentPortalCreateExternalCard } from './ui/paymentPortal';
import {
    fetchPaymentSetupData,
    paymentSetupCreatePrimaryJob,
} from './ui/paymentSetup';
import {
    fetchPerpaySplitSetUpPaymentsData,
    perpaySplitPrimaryJob,
    perpaySplitSalaryInfo,
    perpaySplitCreateBank,
} from './ui/perpaySplitSetUpPayments';
import {
    pinwheelCreatePrimaryJob,
    pinwheelUpdateSalaryInfo,
} from './ui/pinwheel';
import {
    pinwheelSplitCreatePrimaryJob,
    pinwheelSplitUpdateSalaryInfo,
} from './ui/pinwheelSplitSetUpPayments';
import {
    loadSupportTool,
    launchSupportTool,
    updateSupportTool,
    shutDownSupportTool,
} from './ui/supportTool';
import {
    replaceAmountToChargeForUpdateDirectDeposit,
    createPrimaryJobSetStepToUpdate,
    updateJobSetStepToUpdate,
    setStepsForUpdateDirectDeposit,
    acceptCreditLimit,
} from './ui/updateDirectDeposit';
import { updateUserInfoForCardOnboardingMVP2 } from './ui/userInfoForCardOnboardingMVP2';
import { createWithdrawalRequestUI } from './ui/withdrawal';
import { initCordovaOnIdentify } from './utils/cordova';
import { nativeLinkReceived } from './utils/linking';

const staticEpics = [
    acceptCreditLimit,
    acknowledgeNotification,
    analyticsBuffer,
    appUpdate,
    bankScreenCreateBank,
    cancelOrderFromModal,
    cardActivate,
    cardCreatePrimaryJob,
    cardPinwheelTransitionToModal,
    cardPinwheelTransitionToModalMVP2,
    cardOnboardingMetalReplaceMaterial,
    cardUpdateSalaryInfo,
    cardUpdateUserInfo,
    checkoutCreateOrder,
    checkoutLoanDecision,
    checkoutUpdateEmployeeID,
    checkoutUpdateOrder,
    checkoutUpdateUserInfo,
    checkoutVerifyIdentity,
    createBank,
    createDeduction,
    createDeposit,
    createEvent,
    createExternalCard,
    createPassword,
    createPaymentPlan,
    createPaystub,
    createPrimaryJobSetStepToUpdate,
    createSurveyResponse,
    createWithdrawalRequest,
    createWithdrawalRequestUI,
    deactivateAccount,
    cardConfirmEmployerSetNextStep,
    disableFeature,
    enrollFeature,
    fetchABTests,
    fetchABTestsV2,
    fetchAccountSummary,
    fetchAds,
    fetchBanks,
    fetchCompanies,
    fetchCompaniesSelfOnboarding,
    fetchCreditLimits,
    fetchCreditScoreHistory,
    fetchCreditScoresSummary,
    fetchDeductions,
    fetchDeductionsForDataModule,
    fetchEnrolledFeatures,
    fetchExternalCards,
    fetchFeatures,
    fetchNotifications,
    fetchOrders,
    fetchOrdersForDataModule,
    fetchPaymentSetupData,
    paymentSetupCreatePrimaryJob,
    fetchPayrollInstructions,
    fetchPerpaySplitSetUpPaymentsData,
    fetchPinwheelEligibility,
    fetchPinwheelSurvey,
    fetchTransactionDetails,
    fetchUserInfo,
    fetchUserStatus,
    fetchWithdrawalBalance,
    fetchWithdrawalBalanceV2,
    fetchAddressSuggestionsForDataModule,
    fetchAddressDetailsForDataModule,
    getABTest,
    getAccountDeactivationEligibility,
    getAccountDeactivationReasons,
    getJobs,
    getOrderCancellationReasons,
    initCordovaOnIdentify,
    initializeIdentity,
    authenticationSuccess,
    login,
    logoutAuthService,
    logoutToken,
    paymentPortalCreateExternalCard,
    perpaySplitCreateBank,
    perpaySplitPrimaryJob,
    perpaySplitSalaryInfo,
    pinwheelCreatePrimaryJob,
    pinwheelSplitCreatePrimaryJob,
    pinwheelSplitTransitionToModal,
    pinwheelSplitUpdateSalaryInfo,
    pinwheelTransitionToModal,
    pinwheelUpdateSalaryInfo,
    pollCardAccountApplicationStatus,
    pollPaystub,
    primaryJob,
    processCardApplication,
    processCardApplicationAddress,
    profileCreateExternalCard,
    recaptchaActive,
    recaptchaReset,
    reEnableFeature,
    refreshOrder,
    refreshToken,
    replaceAmountToChargeForUpdateDirectDeposit,
    reportABTestVariant,
    reportABTestVariantForDataModule,
    reportError,
    resendMFA,
    resetPassword,
    routeToJwtLocation,
    routeToLocation,
    salaryInfo,
    setPhone,
    setStepsForUpdateDirectDeposit,
    signup,
    updateCardAutoPayStepAfterDateSelection,
    updateCardAutoPayStateAfterFrequencySelection,
    updateDeduction,
    updateDeductionAccountId,
    updateDirectDepositPinwheelTransitionToModal,
    updateEmploymentUI,
    updateJobSetStepToUpdate,
    updatePaycycleUI,
    updateUserInfo,
    verifyBank,
    verifyIdentity,
    verifyPhone,
    verifyMFA,
    verifySelectBank,
    verifySingleBank,
    cancelAutoPaySchedule,
    fetchCoreSummary,
    createAutoPaySchedule,
    createAutoPayScheduleForCardAutoPay,
    createBankAccount,
    createBankAccountForCardAutoPay,
    createCardAccountApplications,
    createPrimaryJob,
    creditUnfreezeCardAccountApplication,
    downloadCardStatement,
    fetchAutoPaySchedules,
    fetchBankAccounts,
    fetchBankAccountsForCardAutoPay,
    fetchBorrowerCreditSummary,
    fetchCardAccountApplicationForOnboarding,
    fetchCardAccountApplication,
    fetchCardAccount,
    fetchCardActivation,
    fetchCardStatements,
    fetchDeductionsForDataModule,
    fetchFeaturesForOnboarding,
    fetchInitiatedAutoPayPayments,
    fetchJobsForDataModule,
    fetchPaymentSetupSummary,
    fetchPayrollInstructionsForDataModule,
    fetchPinwheelABTest,
    fetchUserInfoForDataModule,
    fetchUserStatusForDataModule,
    gateCardAccount,
    gateCardApply,
    fetchGeneralAccountSummary,
    fetchReferralStats,
    updateAutoPaySchedule,
    updateCardAccountApplication,
    updateCardAccountApplicationForOnboarding,
    updateCreditLimit,
    updateJob,
    updateUserInfoForDataModule,
    updateUserInfoForCardOnboardingMVP2,
    uploadCardDocuments,
    validateAddressesForMVP2,
    fetchBorrowerCredits,
    fetchFeatureEnrollments,
    createFeatureEnrollment,
    createFeatureEnrollmentForPerpayPlus,
    fetchFeatureEnrollmentsForPerpayPlus,
    fetchActivePayments,
    fetchCardActivity,
    analyticsIterablePushNotificationOpen,
    navigateOnCordovaUrlPush,
    navigateOnNativeUrlPush,
    nativeLinkReceived,
    postAuthRedirect,
    fetchCardDashboardSummary,
    fetchMarketplaceDashboardSummary,
    fetchPlatformDashboardNotifications,
    actionsToNativeFromWeb,
    actionsToWebFromNative,
    fetchPerpayPlusDashboardSummary,
    fetchDirectDepositDashboardSummary,
    fetchLearnMore,
    fetchTrustPilotLink,
    hookActionEpic,
    partnerOnboardingSignup,
    verifyPartnerOnboardingCode,
    cleanupMagentoTokenOnLogout,
    cleanupAlgoliaOnLogout,
    partnerOnboardingSubmitCardApplication,
    fetchPartnerHostedCardApplication,
    partnerOnboardingPollApplicationStatus,
    fetchFeatureSwitch,
    loadSupportTool,
    launchSupportTool,
    updateSupportTool,
    shutDownSupportTool,
    cardApplicationUpdateUserInfo,
    cardApplicationUpdateUserInfoSuccess,
    submitCardApplication,
    submitCardApplicationError,
    pollCardApplicationStatus,
    pollCardApplicationStatusRedirect,
    pollCardApplicationStatusTimeoutRedirect,
    updateUserInfoContextSuccess,
    validateAddressForCardMigration,
    handleCardMigrationSuccess,
    fetchAuthTokensSuccess,
    passkeyAuthenticateCompleteSuccess,
    passkeyRegisterLogin,
    passkeyRegisterSignup,
    openBiometricRegistrationFailureModal,
    loginWaf,
    wafCaptchaActive,
    signupWaf,
    submitDefaultAddress,

    // --

    cardAccountApplicationCrudDataModule.composedEpic,
    cardOnboardingSwitcherDataModule.composedEpic,
    cardManualPaymentSetupDataModule.composedEpic,
    cardStatementsDataModule.composedEpic,
    directDepositDetailsDataModule.composedEpic,
    ordersDataModule.composedEpic,
    cardPaymentSummaryDataModule.composedEpic,
    userInfoForCardOnboardingMVP2CrudDataModule.composedEpic,
    cardCreateAutoPayDataModule.composedEpic,
    bankAccountsForCardAutoPayDataModule.composedEpic,
    payrollInstructionsFlowDataModule.composedEpic,
    directDepositUpdateEmployerAndAmountDataModule.composedEpic,
    fetchCardDetailsV2DataModule.composedEpic,
    directDepositUpdateManualPaymentSetupDataModule.composedEpic,
    updateDirectDepositDataModule.composedEpic,
    perpayPlusDataModule.composedEpic,
    borrowerCreditsDataModule.composedEpic,
    marketplaceDetailsDataModule.composedEpic,
    featureEnrollmentsForPerpayPlusCrudDataModule.composedEpic,
    cardActivityDataModule.composedEpic,
    checkoutDataModule.composedEpic,
    dashboardV2DataModule.composedEpic,
    marketplaceDetailsNotificationsDataModule.composedEpic,
    cardDetailsNotificationsDataModule.composedEpic,
    pinwheelDataModule.composedEpic,
    partnerOnboardingDataModule.composedEpic,
];

let epicManager = null;

export function getEpicManager() {
    if (!epicManager) {
        epicManager = new EpicManager(staticEpics);
    }

    return epicManager;
}
